import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, Plus, Settings, AlertTriangle, CheckCircle, DollarSign, FileText } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface District {
  id: string;
  name: string;
  domain?: string;
  state?: string;
  subscription_status: string;
  subscription_plan: string;
  trial_end_date?: string;
  max_users?: number;
  created_at: string;
  updated_at: string;
  features_enabled?: string[];
}

interface DistrictForm {
  name: string;
  domain: string;
  state: string;
  subscription_plan: string;
  subscription_status: string;
  max_users: number;
  features_enabled: string[];
  contact_name: string;
  contact_email: string;
  contact_title: string;
  contact_phone: string;
}

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const SUBSCRIPTION_PLANS = ['free', 'basic', 'premium', 'enterprise'];
const SUBSCRIPTION_STATUSES = ['trial', 'active', 'past_due', 'cancelled', 'suspended'];
const AVAILABLE_FEATURES = [
  'course_management',
  'video_library',
  'mentoring_program',
  'advanced_analytics',
  'custom_branding',
  'api_access',
  'priority_support'
];

export default function DistrictManagement() {
  const { user } = useAuth();
  const [districts, setDistricts] = useState<District[]>([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<DistrictForm>({
    name: '',
    domain: '',
    state: '',
    subscription_plan: 'free',
    subscription_status: 'trial',
    max_users: 100,
    features_enabled: [],
    contact_name: '',
    contact_email: '',
    contact_title: '',
    contact_phone: ''
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDistrict, setEditingDistrict] = useState<District | null>(null);

  // Check if user is uber admin (Matt Monjan)
  const isUberAdmin = user?.email === 'matt.monjan@infusedu.com';

  useEffect(() => {
    if (isUberAdmin) {
      loadDistricts();
    }
  }, [isUberAdmin]);

  const loadDistricts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('districts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDistricts(data || []);
    } catch (error) {
      console.error('Error loading districts:', error);
      toast.error('Failed to load districts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const districtData = {
        name: formData.name,
        domain: formData.domain,
        state: formData.state,
        subscription_plan: formData.subscription_plan,
        subscription_status: formData.subscription_status,
        max_users: formData.max_users,
        features_enabled: formData.features_enabled,
        contact_name: formData.contact_name,
        contact_email: formData.contact_email,
        contact_title: formData.contact_title,
        contact_phone: formData.contact_phone
      };

      if (editingDistrict) {
        const { error } = await supabase
          .from('districts')
          .update(districtData)
          .eq('id', editingDistrict.id);

        if (error) throw error;
        toast.success('District updated successfully');
      } else {
        const { error } = await supabase
          .from('districts')
          .insert([districtData]);

        if (error) throw error;
        toast.success('District created successfully');
      }

      setIsDialogOpen(false);
      setEditingDistrict(null);
      resetForm();
      loadDistricts();
    } catch (error) {
      console.error('Error saving district:', error);
      toast.error('Failed to save district');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      domain: '',
      state: '',
      subscription_plan: 'free',
      subscription_status: 'trial',
      max_users: 100,
      features_enabled: [],
      contact_name: '',
      contact_email: '',
      contact_title: '',
      contact_phone: ''
    });
  };

  const editDistrict = (district: District) => {
    setEditingDistrict(district);
    setFormData({
      name: district.name,
      domain: district.domain || '',
      state: district.state || '',
      subscription_plan: district.subscription_plan,
      subscription_status: district.subscription_status,
      max_users: district.max_users || 100,
      features_enabled: district.features_enabled || [],
      contact_name: (district as any).contact_name || '',
      contact_email: (district as any).contact_email || '',
      contact_title: (district as any).contact_title || '',
      contact_phone: (district as any).contact_phone || ''
    });
    setIsDialogOpen(true);
  };

  const toggleDistrictStatus = async (district: District) => {
    try {
      const newStatus = district.subscription_status === 'active' ? 'suspended' : 'active';
      
      const { error } = await supabase
        .from('districts')
        .update({ subscription_status: newStatus })
        .eq('id', district.id);

      if (error) throw error;
      
      toast.success(`District ${newStatus === 'active' ? 'activated' : 'suspended'}`);
      loadDistricts();
    } catch (error) {
      console.error('Error updating district status:', error);
      toast.error('Failed to update district status');
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive'> = {
      active: 'default',
      trial: 'secondary',
      suspended: 'destructive',
      cancelled: 'destructive',
      past_due: 'destructive'
    };

    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const getPlanBadge = (plan: string) => {
    const colors: Record<string, string> = {
      free: 'bg-gray-100 text-gray-800',
      basic: 'bg-blue-100 text-blue-800',
      premium: 'bg-purple-100 text-purple-800',
      enterprise: 'bg-green-100 text-green-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[plan] || 'bg-gray-100 text-gray-800'}`}>
        {plan}
      </span>
    );
  };

  if (!isUberAdmin) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
              <p className="text-muted-foreground">
                This page is only accessible to uber administrators.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Building2 className="h-8 w-8" />
            District Management
          </h1>
          <p className="text-muted-foreground">
            Manage all districts, their access, payment status, and configurations
          </p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => {
              setEditingDistrict(null);
              resetForm();
            }}>
              <Plus className="h-4 w-4 mr-2" />
              Add District
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingDistrict ? 'Edit District' : 'Add New District'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <Tabs defaultValue="basic" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="basic">Basic Info</TabsTrigger>
                  <TabsTrigger value="subscription">Subscription</TabsTrigger>
                  <TabsTrigger value="contact">Contact</TabsTrigger>
                </TabsList>
                
                <TabsContent value="basic" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">District Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="domain">Domain</Label>
                      <Input
                        id="domain"
                        value={formData.domain}
                        onChange={(e) => setFormData({...formData, domain: e.target.value})}
                        placeholder="district.edu"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="state">State</Label>
                    <Select value={formData.state} onValueChange={(value) => setFormData({...formData, state: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map(state => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </TabsContent>
                
                <TabsContent value="subscription" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="subscription_plan">Subscription Plan</Label>
                      <Select value={formData.subscription_plan} onValueChange={(value) => setFormData({...formData, subscription_plan: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SUBSCRIPTION_PLANS.map(plan => (
                            <SelectItem key={plan} value={plan}>{plan}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="subscription_status">Subscription Status</Label>
                      <Select value={formData.subscription_status} onValueChange={(value) => setFormData({...formData, subscription_status: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SUBSCRIPTION_STATUSES.map(status => (
                            <SelectItem key={status} value={status}>{status}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="max_users">Max Users</Label>
                    <Input
                      id="max_users"
                      type="number"
                      value={formData.max_users}
                      onChange={(e) => setFormData({...formData, max_users: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  
                  <div>
                    <Label>Features Enabled</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {AVAILABLE_FEATURES.map(feature => (
                        <div key={feature} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={feature}
                            checked={formData.features_enabled.includes(feature)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  features_enabled: [...formData.features_enabled, feature]
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  features_enabled: formData.features_enabled.filter(f => f !== feature)
                                });
                              }
                            }}
                          />
                          <Label htmlFor={feature} className="text-sm">
                            {feature.replace('_', ' ')}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="contact" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="contact_name">Contact Name</Label>
                      <Input
                        id="contact_name"
                        value={formData.contact_name}
                        onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_title">Contact Title</Label>
                      <Input
                        id="contact_title"
                        value={formData.contact_title}
                        onChange={(e) => setFormData({...formData, contact_title: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="contact_email">Contact Email</Label>
                      <Input
                        id="contact_email"
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_phone">Contact Phone</Label>
                      <Input
                        id="contact_phone"
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                      />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
              
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingDistrict ? 'Update' : 'Create'} District
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Districts</p>
                <p className="text-2xl font-bold">{districts.length}</p>
              </div>
              <Building2 className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Districts</p>
                <p className="text-2xl font-bold text-green-600">
                  {districts.filter(d => d.subscription_status === 'active').length}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Trial Districts</p>
                <p className="text-2xl font-bold text-blue-600">
                  {districts.filter(d => d.subscription_status === 'trial').length}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Issues</p>
                <p className="text-2xl font-bold text-red-600">
                  {districts.filter(d => ['past_due', 'suspended', 'cancelled'].includes(d.subscription_status)).length}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Districts Overview</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading districts...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>District Name</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Max Users</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {districts.map((district) => (
                  <TableRow key={district.id}>
                    <TableCell className="font-medium">
                      <div>
                        <div>{district.name}</div>
                        {district.domain && (
                          <div className="text-sm text-muted-foreground">{district.domain}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{district.state || 'N/A'}</TableCell>
                    <TableCell>{getStatusBadge(district.subscription_status)}</TableCell>
                    <TableCell>{getPlanBadge(district.subscription_plan)}</TableCell>
                    <TableCell>{district.max_users || 'Unlimited'}</TableCell>
                    <TableCell>{new Date(district.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => editDistrict(district)}
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                        <Button
                          variant={district.subscription_status === 'active' ? 'destructive' : 'default'}
                          size="sm"
                          onClick={() => toggleDistrictStatus(district)}
                        >
                          {district.subscription_status === 'active' ? 'Suspend' : 'Activate'}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}